import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportCreatorI18n } from '../i18n';

const getGroupedFields = (fields) => {
  const groups = {};
  fields.forEach((field) => {
    const entityName = field.entity || 'General';
    if (!groups[entityName]) groups[entityName] = [];
    groups[entityName].push(field);
  });

  Object.keys(groups).forEach((groupKey) => {
    groups[groupKey].sort((left, right) =>
      String(left.label || '').localeCompare(String(right.label || ''), 'es')
    );
  });

  return groups;
};

const toDomSafeId = (value) =>
  String(value || 'group')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-');

const FieldsPanel = ({
  DesignSystemComponents,
  search,
  onSearchChange,
  fields,
}) => {
  const { Input } = DesignSystemComponents ?? {};
  const { t } = useTranslation(undefined, { i18n: reportCreatorI18n });
  const [expandedEntity, setExpandedEntity] = useState('');

  const filteredFields = useMemo(() => {
    const term = String(search || '')
      .trim()
      .toLowerCase();
    if (!term) return fields;
    return fields.filter(
      (field) =>
        String(field.label || '')
          .toLowerCase()
          .includes(term) ||
        String(field.entity || '')
          .toLowerCase()
          .includes(term)
    );
  }, [fields, search]);

  const grouped = useMemo(
    () => getGroupedFields(filteredFields),
    [filteredFields]
  );
  const entities = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  useEffect(() => {
    if (!entities.length) {
      setExpandedEntity('');
      return;
    }
    if (!expandedEntity || !entities.includes(expandedEntity)) {
      setExpandedEntity(entities[0]);
    }
  }, [entities, expandedEntity]);

  return (
    <div className='report-creator-panel report-fields-panel'>
      <div className='report-fields-panel-head'>
        <h3 className='fw-semibold mb-1'>{t('rc_fields_title')}</h3>
        <p className='caption color-gray-600 mb-0'>
          {t('rc_fields_help')}
        </p>
      </div>

      {Input ? (
        <Input
          icon='search'
          placeholder={t('rc_fields_search_placeholder')}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      ) : (
        <input
          className='form-control'
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('rc_fields_search_placeholder')}
        />
      )}

      <div className='report-fields-groups mt-3'>
        {entities.length === 0 && (
          <p className='caption color-gray-600 mb-0'>
            {t('rc_fields_empty')}
          </p>
        )}

        {entities.map((entity) => {
          const groupId = `group-${toDomSafeId(entity)}-chips`;
          const isExpanded = expandedEntity === entity;
          return (
            <div
              key={entity}
              className={`report-fields-group ${isExpanded ? 'is-expanded' : ''}`}
            >
              <button
                type='button'
                className='report-fields-group-header'
                onClick={() =>
                  setExpandedEntity((prev) => (prev === entity ? '' : entity))
                }
                aria-expanded={isExpanded}
                aria-controls={groupId}
              >
                <span className='report-fields-group-title'>{entity}</span>
                <span className='report-fields-group-meta'>
                  <span className='report-fields-group-count'>
                    {grouped[entity]?.length || 0}
                  </span>
                  <span
                    className={`report-fields-group-chevron ${isExpanded ? 'open' : ''}`}
                  >
                    &gt;
                  </span>
                </span>
              </button>

              {isExpanded && (
                <div id={groupId} className='report-fields-group-body'>
                  {grouped[entity]?.length ? (
                    grouped[entity].map((field) => (
                      <button
                        key={field.fieldKey}
                        type='button'
                        className='report-field-chip caption'
                        draggable
                        aria-label={t('rc_fields_drag_aria', { label: field.label })}
                        onDragStart={(event) => {
                          event.dataTransfer.setData(
                            'application/json',
                            JSON.stringify({
                              source: 'catalog',
                              fieldKey: field.fieldKey,
                            })
                          );
                          event.dataTransfer.effectAllowed = 'copy';
                        }}
                      >
                        <span>{field.label}</span>
                      </button>
                    ))
                  ) : (
                    <p className='caption color-gray-600 mb-0'>{t('rc_fields_group_empty')}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FieldsPanel;
